import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {
  getExplicitConfigVariables,
  getSchemaFieldTypeAndMultiFromVariableSpec,
  isExemptFromSchemaFieldMapping,
} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {
  VariableType,
  CompatibilityCheckFn,
  CheckResultLevel,
  VARIABLE_TYPE_TO_NAME,
  TemplateVariablesByType,
} from './CompatibilityCheckTypes';

/**
 * Allowed number of a variable. Used by `getBasicCheckFn` for building basic checks.
 *
 * @package
 */
export enum VariableAmount {
  ZERO,
  EXACTLY_ONE,
  ANY_NUMBER,
}

const VARIABLE_AMOUNT_TO_FN: Record<VariableAmount, (array: Array<unknown>) => boolean> = {
  [VariableAmount.ZERO]: (array) => array.length === 0,
  [VariableAmount.EXACTLY_ONE]: (array) => array.length === 1,
  [VariableAmount.ANY_NUMBER]: () => true,
};

/**
 * Build a basic compatibility check function based on the type of variable.
 *
 * @package
 */
export function getBasicCheckFn(type: VariableType, amount: VariableAmount): CompatibilityCheckFn {
  const name = VARIABLE_TYPE_TO_NAME[type];
  const message =
    amount === VariableAmount.ZERO
      ? `Must have no ${name}s`
      : amount === VariableAmount.EXACTLY_ONE
        ? `Must have exactly one ${name}`
        : `May have any number of ${name}s`;
  const passFn = (variables: TemplateVariablesByType): boolean =>
    VARIABLE_AMOUNT_TO_FN[amount](variables[type]);
  return (variables: TemplateVariablesByType) => ({
    message,
    level: passFn(variables) ? CheckResultLevel.PASS : CheckResultLevel.FAIL,
  });
}

/**
 * Warns if one or more table variables require the user to map columns before running the template.
 *
 * @package
 */
export const WARN_REQUIRES_COLUMN_MAPPING: CompatibilityCheckFn = (variables) => {
  const count = variables[VariableType.TABLE].filter(
    (variable) => variable.replaceDataFrameColumnsSpec.length > 0
  ).length;

  if (count === 0) {
    return null;
  }

  return {
    level: CheckResultLevel.WARN,
    message:
      count === 1
        ? 'This template has a table variable which requires mapping columns before running the template.'
        : `This template has ${count} table variables which require mapping columns before running the template.`,
  };
};

/**
 * Warns if a multi-select variable will be mapped into a single-select schema field value.
 *
 * @package
 */
export const WARN_MULTI_SELECT_PRIMITIVE_FIELD: CompatibilityCheckFn = (variables) => {
  const multiSelectPrimitiveVariable = variables[VariableType.FIELD].find((variable) => {
    const spec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
    if (spec.kind !== 'array') {
      return false;
    }
    const schemaField = getSchemaFieldTypeAndMultiFromVariableSpec(spec);
    return schemaField != null && !schemaField.isMulti;
  });

  if (multiSelectPrimitiveVariable == null) {
    return null;
  }

  return {
    level: CheckResultLevel.WARN,
    message: `"${multiSelectPrimitiveVariable.name}" is multi-select, but the run schema field will be single-select.`,
  };
};

/**
 * Warns if a domain variable (e.g. Plate) will be mapped into an "Any inventory" schema field.
 *
 * (Schema field can't be restricted to a specific inventory type, e.g. "Any plate".)
 *
 * @package
 */
export const WARN_STORABLE_VARIABLE_WITHOUT_SCHEMA: CompatibilityCheckFn = (variables) => {
  const storableVariableWithoutSchema = variables[VariableType.FIELD].find((variable) => {
    const spec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
    const schemaField = getSchemaFieldTypeAndMultiFromVariableSpec(spec);
    return (
      schemaField != null &&
      schemaField.fieldType === FieldType.STORABLE_LINK &&
      schemaField.schemaId == null &&
      schemaField.schemaInterfaceId == null
    );
  });

  if (storableVariableWithoutSchema == null) {
    return null;
  }

  return {
    level: CheckResultLevel.WARN,
    message: `Because "${storableVariableWithoutSchema.name}" has no schema selected, the run schema field will be "Any inventory".`,
  };
};

/**
 * Validates field variables can be converted to a schema field, which is required by the Run schema
 * integration.
 *
 * @package
 */
export const CHECK_CAN_CONVERT_TO_SCHEMA_FIELDS: CompatibilityCheckFn = (variables) => {
  const unconvertibleVariableNames = getExplicitConfigVariables(variables[VariableType.FIELD])
    .filter((variable) => {
      const spec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
      return (
        !isExemptFromSchemaFieldMapping({variableTypeConfigSpec: spec}) &&
        getSchemaFieldTypeAndMultiFromVariableSpec(spec) == null
      );
    })
    .map((variable) => variable.name);

  if (unconvertibleVariableNames.length === 0) {
    return null;
  }

  return {
    level: CheckResultLevel.FAIL,
    message: `One or more field variables cannot be converted to schema fields: ${unconvertibleVariableNames.join(', ')}.`,
  };
};
